import { useEffect, useMemo, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import ProductSchema from "../components/ProductSchema";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { supabase } from "../supabaseClient";

import CosmicBackground from "../components/CosmicBackground";
import HeroSection from "../components/HeroSection";
import GlobalTrustPoints from "../components/global/GlobalTrustPoints";
import GlobalUseCases from "../components/global/GlobalUseCases";
import FeaturesSection from "../components/FeaturesSection";
import GlobalJourney from "../components/global/GlobalJourney";
import GlobalSteps from "../components/global/GlobalSteps";
import appChatImg from "../assets/global/AppBanner_1_3.webp";
import appCallImg from "../assets/global/AppBanner_2_3.webp";
import appReportImg from "../assets/global/AppBanner_3_3.webp";
import horoscopeImg from "../assets/global/essential_horoscope.webp";
import compatImg from "../assets/global/compatibiltybannerhome.webp";
import shopImg from "../assets/global/shoppingbanner.webp";
import premiumImg from "../assets/global/subscriptionbannerhome.webp";
import FaqSection, { defaultFaqData } from "../components/FaqSection";
import TestimonialsSection from "../components/TestimonialsSection";
import { internationalTestimonials } from "../data/internationalTestimonials";
import MobileAppSection from "../components/MobileAppSection";
import AuthModal from "../components/AuthModal";
import TarotSection from "../components/TarotSection";
import {
  IconCalendarStats,
  IconBook2,
  IconMessages,
  IconLock,
  IconGift,
} from "@tabler/icons-react";
import styles from "./GlobalPage.module.css";

const PENDING_KUNDLI_DETAILS_KEY = "pendingKundliDetails";

// English prompts for the USA audience (faithful translations of the
// home/market Hindi/Hinglish set). Drives the animated hero search bar.
const GLOBAL_QUESTIONS = [
  "When will I get married",
  "When will I get a job",
  "Why is my marriage getting so delayed",
  "Tell me about my career",
  "When will my marriage happen",
  "How will my day go today",
];

// Feature-rail cards for the USA landing (images ported from the Aura AI app).
// Each card routes logged-in users to the screen; logged-out users hit the
// existing auth/login flow (handled in GlobalPage via onCardClick).
const GLOBAL_CARDS = [
  { img: appChatImg, title: "Chat with Aura AI", sub: "Ask anything, get instant answers", route: "/chat" },
  { img: horoscopeImg, title: "Daily Horoscope", sub: "Your personalized cosmic forecast", route: "/chat" },
  { img: appCallImg, title: "Talk to Aura AI", sub: "Live voice guidance, 24/7", route: "/chat?startCall=1" },
  { img: appReportImg, title: "Free Birth Chart", sub: "Your detailed Vedic report", route: "/reports" },
  { img: compatImg, title: "Love Compatibility", sub: "Match your stars with theirs", route: "/reports" },
  { img: shopImg, title: "Remedy Store", sub: "Rudraksha, gemstones & remedies", route: "/store" },
  { img: premiumImg, title: "Aura AI Premium", sub: "Unlock deeper guidance", route: "/wallet" },
];

declare global {
  interface Window {
    Razorpay: any;
  }
}

/**
 * /global landing page (USA marketing). Replica of /market: mirrors the homepage
 * sections + behaviour (auth, the pending-Kundli post-login flow, notification
 * prompt, push re-registration, hash scroll) with the marketing copy/sections
 * layered on top. USA-specific changes will diverge from /market over time.
 */
export default function GlobalPage({
  withTarot = false,
}: {
  /** The homepage tarot deck under the hero (/uae only). It still hides
      itself in India, so this only adds it for visitors abroad. */
  withTarot?: boolean;
} = {}) {
  const navigate = useNavigate();
  const location = useLocation();
  const faqRef = useRef<HTMLElement>(null);

  const {
    user: authUser,
    signInWithGoogle,
    requestPhoneOtp,
    verifyPhoneOtp,
    requestFirebasePhoneOtp,
    verifyFirebasePhoneOtp,
    verifyMsg91Otp,
    planTier,
    checkingStatus,
    pricingVariant,
    refreshUserStatus,
    userProfiles,
  } = useAuth();

  const user = authUser;

  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);

  // ---- Auth handlers (identical to HomePage, incl. A/B pricing variant) ----
  const phoneAuthHandlers = useMemo(
    () => ({ requestOtp: requestPhoneOtp, verifyOtp: verifyPhoneOtp }),
    [requestPhoneOtp, verifyPhoneOtp]
  );

  const firebasePhoneAuthHandlers = useMemo(
    () => ({
      requestOtp: requestFirebasePhoneOtp,
      verifyOtp: verifyFirebasePhoneOtp,
    }),
    [requestFirebasePhoneOtp, verifyFirebasePhoneOtp]
  );

  const msg91AuthHandlers = useMemo(
    () => ({
      verifyOtp: (payload: {
        accessToken: string;
        phone: string;
        firstName?: string;
        lastName?: string;
        email?: string;
      }) => verifyMsg91Otp({ ...payload, variant: pricingVariant }),
    }),
    [verifyMsg91Otp, pricingVariant]
  );

  // ---- Mark this session as the "market" experience ----
  // Reuses the existing market-mode machinery (Navbar homeTo, FooterEmailBanner
  // CTA footer, CTA pulse) so /global behaves identically to /market for now.
  // Split into its own mode when the USA experience needs to diverge.
  useEffect(() => {
    try {
      sessionStorage.setItem("auraMode", "global");
    } catch {
      /* sessionStorage unavailable */
    }
    // body class scopes the attention-pulse to the hero + Kundli CTAs here only
    document.body.classList.add("market-mode");
    document.body.classList.add("global-mode");
    return () => {
      document.body.classList.remove("market-mode");
      document.body.classList.remove("global-mode");
    };
  }, []);

  // ---- Scroll to #faq when linked ----
  useEffect(() => {
    if (location.hash === "#faq" && faqRef.current) {
      faqRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [location]);

  // ---- Create the pending Kundli profile after the user signs in ----
  useEffect(() => {
    if (user && !checkingStatus && userProfiles) {
      const pendingDetailsJSON = sessionStorage.getItem(
        PENDING_KUNDLI_DETAILS_KEY
      );

      if (pendingDetailsJSON) {
        // Multiple Kundli profiles are allowed for everyone — no subscription
        // required — so we proceed straight to creating the profile.
        sessionStorage.removeItem(PENDING_KUNDLI_DETAILS_KEY);

        const createPendingProfile = async () => {
          try {
            const details = JSON.parse(pendingDetailsJSON);

            const { data: profileData, error: profileError } = await supabase
              .from("user_profiles")
              .insert({ name: details.name, user_id: user.id })
              .select()
              .single();

            if (profileError) throw profileError;

            const { error: birthDetailsError } = await supabase
              .from("user_birth_details")
              .insert({
                profile_id: profileData.id,
                date_of_birth: details.date_of_birth,
                time_of_birth: details.time_of_birth,
                gender: details.gender,
                birth_place: details.birth_place,
                birth_lat: details.birth_lat,
                birth_lng: details.birth_lng,
                birth_timezone: details.birth_timezone,
              });

            if (birthDetailsError) throw birthDetailsError;

            await refreshUserStatus();
            navigate("/reports");
          } catch (error) {
            console.error("Failed to save pending Kundli details:", error);
            alert(
              "There was an issue creating your Kundli profile. Please try again from the homepage."
            );
          }
        };

        createPendingProfile();
      }
    }
  }, [
    user,
    checkingStatus,
    userProfiles,
    planTier,
    navigate,
    refreshUserStatus,
  ]);

  // ---- Silent push re-registration for users who already granted permission ----
  useEffect(() => {
    const reRegisterExistingUsers = async () => {
      // @ts-ignore - CleverTap types not available
      const clevertap = (window as any).clevertap;

      const hasReRegistered = localStorage.getItem("ct_push_reregistered");
      if (hasReRegistered) return;

      if (
        typeof Notification === "undefined" ||
        Notification.permission !== "granted"
      ) {
        return;
      }

      if (!clevertap?.notifications?.push) return;

      try {
        await clevertap?.notifications.push({
          titleText: "Notifications Updated",
          bodyText: "Your notification settings have been improved.",
          skipDialog: true,
          serviceWorkerPath: "/clevertap_sw.js",
          vapidPublicKey:
            "BBP6tWBZCnyNnN39wXWHaKJP_K2WbDM4034HIr2Md_aTb36mqkkldoFP_rxh55v-_G1rIxSZE0Q6rEMXIT5pVuo",
        });

        localStorage.setItem("ct_push_reregistered", "true");

        if (clevertap?.profile?.push) {
          clevertap?.profile.push({
            Site: {
              push_enabled: true,
              re_registered: true,
              re_registered_at: new Date().toISOString(),
            },
          });
        }
      } catch (error) {
        console.warn("[Re-registration] Failed to re-register user:", error);
      }
    };

    const timer = setTimeout(() => {
      reRegisterExistingUsers();
    }, 5000);

    return () => clearTimeout(timer);
  }, []);

  // Keep the first two homepage FAQs, replace the rest with the boss copy.
  const marketFaq = useMemo(
    () => [
      defaultFaqData[0],
      defaultFaqData[1],
      {
        question: "Is this only a daily horoscope app?",
        answer:
          "No. Aura AI is more than daily horoscope. It lets you ask personal questions and get answers based on your birth chart.",
        icon: <IconCalendarStats size={20} />,
      },
      {
        question: "Do I need astrology knowledge?",
        answer: "No. Aura explains everything in simple language.",
        icon: <IconBook2 size={20} />,
      },
      {
        question: "Can I ask follow-up questions?",
        answer:
          "Yes. You can continue the conversation and ask follow-up questions.",
        icon: <IconMessages size={20} />,
      },
      {
        question: "Is my data private?",
        answer:
          "Yes. Your birth details and personal questions are kept private and secure.",
        icon: <IconLock size={20} />,
      },
      {
        question: "Is the first chat free?",
        answer: "Yes. You can start with your first free question.",
        icon: <IconGift size={20} />,
      },
    ],
    []
  );

  const handleCardClick = (route: string) => {
    if (user) {
      navigate(route);
    } else {
      setIsAuthModalOpen(true);
    }
  };

  return (
    <main
      className={styles.global}
      style={{ position: "relative", isolation: "isolate" }}
    >
      <ProductSchema />
      <Helmet>
        {/* This page carried the exact title AND description of /market, and its
            H1 came from HeroSection's default, so it also duplicated the
            homepage heading. Three-way collision on the one page meant to serve
            international visitors. Both are now specific to that audience. */}
        {/* Retargeted to "AI astrology" on 10 Aug 2026. This page previously
            said "Vedic Astrology Online" and never used the word AI, which was
            backwards: every top-20 query this site receives is an AI-astrology
            variant, and the US alone sends ~8,800 impressions a month on those
            terms. Chasing "vedic astrologer <country>" meant competing on
            queries the domain has no topical signal for, while ignoring the
            category it already ranks for. The geo copy below is unchanged. */}
        <title>
          AI Astrology Online, Free Chat Worldwide | Aura AI 24x7
        </title>
        <meta
          name="description"
          content="Chat with an AI Vedic astrologer from anywhere in the world, free to start. Aura AI builds your kundli from your real birth city and timezone, then answers 24x7."
        />
        <link rel="canonical" href="https://astroaura.ai/global" />
        <meta property="og:image" content="https://astroaura.ai/og-image.png" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content="https://astroaura.ai/og-image.png" />
      </Helmet>

      <CosmicBackground />


      <div style={{ position: "relative", width: "100%", zIndex: 1 }}>
        <HeroSection
          user={user}
          onGoogleSignIn={signInWithGoogle}
          phoneAuth={phoneAuthHandlers}
          msg91Auth={msg91AuthHandlers}
          // "AI Astrology" not "Vedic Astrology", to match the retargeted title.
          // Still distinct from the homepage H1 ("Free AI Astrology Chat,
          // Answered") and /market ("Ask Aura AI, Your AI Astrologer"), so the
          // three pages do not compete for the same query.
          headingLead="AI Astrology,"
          headingAccent="Wherever You Are"
        />
      </div>

      <div
        style={{
          backgroundColor: "transparent",
          marginTop: "-1px",
          position: "relative",
          zIndex: 1,
        }}
      >
        {withTarot ? (
          <TarotSection onRequestLogin={() => setIsAuthModalOpen(true)} />
        ) : null}
        <FeaturesSection questions={GLOBAL_QUESTIONS} withImages />
      </div>

      {/* PROBLEM + SOLUTION + HOW IT WORKS (OG /market journey, /global-revamped problem card) */}
      <GlobalJourney />

      {/* HERO TRUST POINTS */}
      <GlobalTrustPoints />

      {/* HOW IT WORKS (moved below trust) */}
      <GlobalSteps />

      {/* USE CASES */}
      <GlobalUseCases />

      <div
        style={{
          backgroundColor: "transparent",
          marginTop: "-1px",
          position: "relative",
          zIndex: 1,
        }}
      >
        <section ref={faqRef}>
          <FaqSection items={marketFaq} />
        </section>
      </div>

      <div
        style={{
          backgroundColor: "transparent",
          marginTop: "-1px",
          position: "relative",
          zIndex: 1,
        }}
      >
        <TestimonialsSection
          items={internationalTestimonials}
          title="Trusted by people looking for real clarity"
          subtitle="Thousands of users are using Aura AI to understand their relationships, career, marriage, family situations, and life path with personalized astrology guidance."
          microcopy="Start with one free question and experience the clarity yourself"
        />
      </div>

      <div
        style={{
          backgroundColor: "transparent",
          marginTop: "-35px",
          position: "relative",
          zIndex: 1,
        }}
      >
        <MobileAppSection
          title={<>Your Personal Astrologer is Now in Your Pocket</>}
          subtitle={
            <>
              Download Aura AI and ask your first question for free. Whether it
              is 2 AM confusion, relationship doubt, career stress, or marriage
              pressure, Aura is available anytime.
            </>
          }
        />
      </div>

      <AuthModal
        isOpen={isAuthModalOpen}
        onClose={() => setIsAuthModalOpen(false)}
        onGoogleSignIn={signInWithGoogle}
        phoneAuth={phoneAuthHandlers}
        firebasePhoneAuth={firebasePhoneAuthHandlers}
        msg91Auth={msg91AuthHandlers}
      />
    </main>
  );
}
